import { useApi } from '@/api';
import { TableSkeleton } from '@/components';
import {
  CurrencyValue,
  DataTable,
  useTableProps,
  RelativeDateTime,
  Box
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

export default function DocumentTable({ ilk }) {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-purchase_date'
  });
  const [data, error] = useApi({
    url: `/rwa/${ilk}/portfolio/document/entries/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Name',
        getCell: (row) => row.name,
        columnSize: ['minmax(100px, auto)', 'minmax(150px, auto)']
      },
      {
        header: 'Type',
        getCell: (row) => row.fixed_income_type_name
      },
      {
        header: 'Maturity',
        getCell: (row) =>
          row.maturity ? <RelativeDateTime value={row.maturity} /> : '-',

        sortingField: 'maturity'
      },
      {
        header: 'Notional',
        getCell: (row) => (
          <CurrencyValue value={row.notional} options={{ currency: 'USD' }} />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'notional'
      },
      {
        header: 'Purchase Price',
        getCell: (row) => (
          <CurrencyValue
            value={row.purchase_price}
            options={{ currency: 'USD' }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'purchase_price'
      },
      {
        header: 'Realized Surplus',
        getCell: (row) => (
          <CurrencyValue
            value={row.realized_surplus}
            options={{ currency: 'USD' }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'realized_surplus'
      },
      {
        header: 'Total Discount',
        getCell: (row) => (
          <CurrencyValue
            value={row.total_discount}
            options={{ currency: 'USD' }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'total_discount'
      },
      {
        header: 'Purchase Proceeds',
        getCell: (row) => (
          <CurrencyValue
            value={row.purchase_proceeds}
            options={{ currency: 'USD' }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'purchase_proceeds'
      },
      {
        header: 'Sales Proceeds',
        getCell: (row) => (
          <CurrencyValue
            value={row.sales_proceeds}
            options={{ currency: 'USD' }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'sales_proceeds'
      },
      {
        header: 'Purchase Date',
        getCell: (row) => (
          <Box color="muted">
            {row.purchase_date ? (
              <RelativeDateTime value={row.purchase_date} />
            ) : (
              '-'
            )}
          </Box>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'purchase_date'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }

  return <TableSkeleton />;
}
